import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Sparkles, Leaf, ShieldCheck, BedDouble, ChevronDown, MapPin } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { CallButton, WhatsAppButton } from "@/components/CTAs";
import Marquee from "@/components/Marquee";
import ServiceCard from "@/components/ServiceCard";
import ReviewsSlider from "@/components/ReviewsSlider";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CONTACT, SERVICES, FAQS, HERO_IMAGE, HERO_SECONDARY, JACUZZI_GROUP } from "@/data/site";

const HERO_LINES = ["Restore Your Body,", "Renew Your Soul"];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-screen items-end overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0">
        <img src={HERO_IMAGE} alt="Reception lounge of Belong Saloon and Spa with gold lotus signage and warm lighting" fetchPriority="high" className="h-[120%] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian/90 via-obsidian/50 to-obsidian/20" />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/60 via-transparent to-obsidian" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-28 pt-40 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-6 text-xs font-medium uppercase tracking-[0.35em] text-gold/90 font-mono"
        >
          Body Massage &amp; Jacuzzi Spa · Suchitra, Hyderabad
        </motion.p>

        <h1 className="font-serif-display text-5xl leading-[1.05] tracking-tight text-cream sm:text-7xl lg:text-8xl">
          {HERO_LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "italic text-gold-gradient" : ""}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.05, delay: 0.25 + i * 0.16, ease: [0.16, 1, 0.3, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-stone-300 sm:text-lg"
        >
          Seven signature services — from Aroma Therapy to the Four Hand Massage and a private Jacuzzi Spa —
          delivered in candlelit suites above Quthbullapur Main Road.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <CallButton testid="hero-call-button" />
          <WhatsAppButton testid="hero-whatsapp-button" />
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 md:block"
      >
        <ChevronDown size={22} className="float-slow text-gold/70" />
      </motion.div>
    </section>
  );
};

const PILLARS = [
  { icon: Sparkles, title: "Certified Therapists", text: "Trained hands, verified technique, pressure tailored to your body." },
  { icon: Leaf, title: "Pure Essential Oils", text: "Cold-pressed botanical blends chosen fresh for every session." },
  { icon: BedDouble, title: "Private Suites", text: "Candlelit, sound-softened rooms. Your session is never rushed." },
  { icon: ShieldCheck, title: "Absolute Hygiene", text: "Fresh linen and sanitised suites for every single guest." },
];

const GALLERY = [
  {
    src: "/images/gallery-reception.jpg",
    alt: "Belong Saloon and Spa reception desk with illuminated gold lotus signage",
    label: "The Welcome Desk",
    cls: "lg:col-span-4 lg:row-span-2",
  },
  {
    src: "/images/gallery-suite.jpg",
    alt: "Private candlelit massage suite at Belong Spa with warm wood panelling",
    label: "Private Suites",
    cls: "lg:col-span-8",
  },
  {
    src: "/images/gallery-corridor.jpg",
    alt: "Gold-accented marble corridor leading to the treatment rooms",
    label: "The Corridor",
    cls: "lg:col-span-4",
  },
  {
    src: "/images/gallery-collage.jpg",
    alt: "Inside Belong Spa — lounge, reception and couple therapy suites",
    label: "Around the Sanctuary",
    cls: "lg:col-span-4",
  },
];

export default function Home() {
  return (
    <main data-testid="home-page" className="pb-16 md:pb-0">
      <Seo
        title="Massage & Spa in Suchitra, Hyderabad | Belong Saloon & Spa"
        description="Belong Saloon & Spa in Suchitra, Hyderabad — aroma therapy, Balinese, deep tissue, Thai, four hand & full body massage, plus Jacuzzi spa. Call 098666 34473."
      />
      <Hero />
      <Marquee />

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="grid items-end gap-8 lg:grid-cols-[1.2fr_1fr]">
          <Reveal>
            <Eyebrow className="mb-4">Our Services</Eyebrow>
            <h2 className="font-serif-display text-3xl leading-tight text-cream sm:text-5xl">
              Seven ways to <span className="italic text-gold-gradient">come back</span> to your body
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-base leading-relaxed text-stone-400">
              Every session begins with a conversation and ends with tea. Choose your service, pick a duration —
              45, 60, 90 or 120 minutes — and let the city wait outside.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.slug} service={s} index={i} />
          ))}
          <Reveal delay={0.2} className="gold-ring flex flex-col justify-between rounded-2xl bg-ember p-8">
            <div>
              <h3 className="font-serif-display text-2xl text-cream">Not sure where to begin?</h3>
              <p className="mt-3 text-sm leading-relaxed text-stone-400">
                Call us — describe how your body feels, and we will match you to the right service and duration.
              </p>
            </div>
            <CallButton testid="services-help-call" label="Ask a therapist" className="mt-8 self-start" />
          </Reveal>
        </div>
      </section>

      <section className="relative overflow-hidden border-y border-gold/10 bg-ember/50 py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal className="relative">
            <div className="gold-ring overflow-hidden rounded-2xl">
              <img src={HERO_SECONDARY} alt="Warm massage oil poured by therapist hands at Belong Spa" loading="lazy" className="h-[420px] w-full object-cover" />
            </div>
            <div className="glow-pulse absolute -bottom-6 -right-4 hidden rounded-2xl border border-gold/30 bg-obsidian px-6 py-5 sm:block">
              <p className="font-serif-display text-4xl text-gold">60+</p>
              <p className="mt-1 text-xs uppercase tracking-[0.2em] text-stone-400">minute signature services</p>
            </div>
          </Reveal>
          <div>
            <Reveal>
              <Eyebrow className="mb-4">Signature Spotlight</Eyebrow>
              <h2 className="font-serif-display text-3xl leading-tight text-cream sm:text-5xl">
                Four hands. One jacuzzi. <span className="italic text-gold-gradient">Zero thoughts.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 text-base leading-relaxed text-stone-400">
                Our two most-loved indulgences begin at 60 minutes: the synchronised Four Hand Massage and the
                private herbal Jacuzzi Spa. Book them back-to-back for the full Belong evening.
              </p>
            </Reveal>
            <Reveal delay={0.25} className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/services-luxury-spa/four-hand-massage"
                data-testid="spotlight-four-hand"
                className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-stone-950 transition-all duration-300 hover:bg-gold-bright active:scale-[0.98]"
              >
                Four Hand Massage
              </Link>
              <Link
                to="/services-luxury-spa/jacuzzi-spa"
                data-testid="spotlight-jacuzzi"
                className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-6 py-3 text-sm font-semibold text-gold transition-all duration-300 hover:bg-gold/10 active:scale-[0.98]"
              >
                Jacuzzi Spa
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <Reveal className="mb-14 text-center">
          <Eyebrow className="mb-4">Why Belong</Eyebrow>
          <h2 className="font-serif-display text-3xl text-cream sm:text-5xl">
            Care you can <span className="italic text-gold-gradient">feel</span>
          </h2>
        </Reveal>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1}>
              <div data-testid={`pillar-${i}`} className="gold-ring gold-ring-hover h-full rounded-2xl bg-coal p-7">
                <p.icon size={26} className="text-gold" />
                <h3 className="mt-5 font-serif-display text-xl text-cream">{p.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-stone-400">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="gallery" data-testid="gallery-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="grid items-end gap-8 lg:grid-cols-[1.2fr_1fr]">
          <Reveal>
            <Eyebrow className="mb-4">The Space</Eyebrow>
            <h2 className="font-serif-display text-3xl leading-tight text-cream sm:text-5xl">
              Step inside <span className="italic text-gold-gradient">the sanctuary</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-base leading-relaxed text-stone-400">
              Warm wood, marble and candlelight — every corner of Belong is built to slow you down
              before the first touch.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-12 lg:auto-rows-[290px]">
          {GALLERY.map((g, i) => (
            <Reveal key={g.src} delay={(i % 3) * 0.1} className={g.cls}>
              <figure
                data-testid={`gallery-img-${i}`}
                className="group gold-ring gold-ring-hover relative h-72 w-full overflow-hidden rounded-2xl sm:h-80 lg:h-full"
              >
                <img
                  src={g.src}
                  alt={g.alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />
                <figcaption className="absolute bottom-4 left-5 font-serif-display text-lg italic text-cream/95">
                  {g.label}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-gold/10 bg-ember/50 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mb-14 text-center">
            <Eyebrow className="mb-4">Guest Reviews</Eyebrow>
            <h2 className="font-serif-display text-3xl text-cream sm:text-5xl">
              Words from the <span className="italic text-gold-gradient">unwound</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <ReviewsSlider />
          </Reveal>
        </div>
      </section>

      <section data-testid="home-faq-section" className="mx-auto max-w-5xl px-5 py-24 sm:px-8 sm:py-32">
        <Reveal className="mb-12 text-center">
          <Eyebrow className="mb-4">Good to Know</Eyebrow>
          <h2 className="font-serif-display text-3xl text-cream sm:text-5xl">
            Questions, <span className="italic text-gold-gradient">answered</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible data-testid="home-faq-accordion" className="grid gap-4 md:grid-cols-2">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`home-faq-${i}`}
                data-testid={`home-faq-item-${i}`}
                className="gold-ring rounded-2xl bg-coal px-6 data-[state=open]:border-gold/40"
              >
                <AccordionTrigger data-testid={`home-faq-trigger-${i}`} className="py-5 text-left font-serif-display text-lg text-cream hover:text-gold hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent data-testid={`home-faq-content-${i}`} className="pb-5 text-sm leading-relaxed text-stone-400">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      <section data-testid="home-spa-cta" className="relative overflow-hidden">
        <img src={JACUZZI_GROUP} alt="Guests relaxing in the Belong Spa jacuzzi" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-obsidian/85" />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-transparent to-obsidian" />
        <div className="relative z-10 mx-auto max-w-4xl px-5 py-28 text-center sm:px-8 sm:py-36">
          <Reveal>
            <Eyebrow className="mb-4">Reserve Your Evening</Eyebrow>
            <h2 className="font-serif-display text-4xl leading-tight text-cream sm:text-6xl">
              Your table is <span className="italic text-gold-gradient">already warm</span>
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-stone-300">
              One call is all it takes. Tell us the service and the hour — we handle everything after that.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <CallButton testid="spa-cta-call" />
              <WhatsAppButton testid="spa-cta-whatsapp" />
            </div>
            <p className="mx-auto mt-10 flex max-w-xl items-start justify-center gap-2 text-sm leading-relaxed text-stone-400">
              <MapPin size={16} className="mt-0.5 shrink-0 text-gold" />
              {CONTACT.address}
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
